/**
 * AdvisorOffer.jsx - subtle 1-on-1 support offer for high-intent SA users.
 *
 * Designed to drop into matcher/tool outputs at moments of genuine
 * friction or high-consideration. Never shown outside SA. Always tracks
 * shown + clicked so we can measure conversion per placement.
 *
 * Usage:
 *   <AdvisorOffer placement="fbw-results" state={state} />
 *
 * Visual treatment is intentionally tiny - one line of copy + icon -
 * because the brand direction is self-serve. This is a quiet escape
 * hatch, not an upsell.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, ArrowRight } from 'lucide-react';
import { trackEvent } from '../../lib/trackEvent';

export const AdvisorOffer = ({
  placement,
  state,
  user,
  variant = 'default',  // 'default' | 'urgent' | 'compact'
  className = '',
}) => {
  // SA-only gating. Anonymous users can still see it (state may come
  // from the matcher account form or the URL).
  const isSA = (state || '').toUpperCase().trim() === 'SA';

  useEffect(() => {
    if (!isSA || !placement) return;
    trackEvent('tool_action', { action: 'advisor_offer_shown', placement }, { user });
  }, [isSA, placement, user]);

  if (!isSA) return null;

  const onClick = () => {
    trackEvent('tool_action', { action: 'advisor_offer_clicked', placement }, { user });
  };

  // Variant copy
  let lead = 'In South Australia? Book a 30-min 1-on-1 with our advisor';
  let sub  = '30-min intro call · paid follow-ups if helpful';
  if (variant === 'urgent') {
    lead = 'Want to talk this through with a human?';
    sub  = 'Complimentary 30-min intro call with our SA advisor';
  } else if (variant === 'compact') {
    lead = 'Or chat 1-on-1 with our SA advisor';
    sub  = '';
  }

  return (
    <Link
      to="/one-on-one-support"
      onClick={onClick}
      className={`advisor-offer advisor-offer--${variant} ${className}`}
      data-testid={`advisor-offer-${placement}`}
    >
      <UserCheck size={16} className="advisor-offer__icon" aria-hidden="true" />
      <div className="advisor-offer__copy">
        <span className="advisor-offer__lead">{lead}</span>
        {sub && <span className="advisor-offer__sub">{sub}</span>}
      </div>
      <ArrowRight size={14} className="advisor-offer__arrow" aria-hidden="true" />
    </Link>
  );
};

export default AdvisorOffer;
